import type { World } from './types';
import { epochDayOf } from './calendar';
import { COASTLINE_KM } from './turtles';
import type { ReefIsland } from './reef';

/**
 * The derelict fishing gear on Maui Nui's shores (M307, the first field mission's design, `docs/FIELD_LOOP_ROADMAP.md`):
 * what lies on each island's shore, read from its last reckoning and the days since at the rate it comes ashore again,
 * so the tick does nothing with it; a shore cleanup sets the reckoning. No gain in the coral, the seals or the turtles
 * is told or simulated from it: the measured effects (coral under nets, Halperin et al. 2023; the seals' entanglements
 * falling after removal, Baker et al. 2024) are the Northwestern Hawaiian Islands' lagoons and atolls, not Maui Nui's
 * shores.
 */

/** A shore's tonnes on a day, an epoch day: the last cleanup's, or the opening's at the world's start. */
export interface ShoreReckoning {
  tonnes: number;
  day: number;
}

/**
 * The derelict fishing gear points PIFSC's 2008 aerial survey recorded by island: "the 2008 survey recorded 110 points"
 * at Maui, Molokaʻi 133, Lānaʻi 291 (PIFSC 2010, SP-10-003, public domain; MEASURED, its own caution that the rise from
 * 2006's "likely reflects the more refined flight methods used in 2008"). The counts are 2008's; nothing newer was
 * reachable.
 */
export const DFG_POINTS_2008 = { maui: 110, molokai: 133, lanai: 291 } as const;

/**
 * A point's tonnes, the Lānaʻi removal's mean pile: "In total, the CRED removed 156 individual DFG piles, totaling 17.4
 * metric tons" (PIFSC 2010, ibid., January 2007), 0.1115 t a pile (MEASURED; each point taken as one pile, JUDGED).
 */
export const PILE_T = 0.1115;

/**
 * Kahoʻolawe's shore, not in the aerial survey: what KIRC airlifted from Kanapou in January 2023, "An estimated five
 * tons of marine debris" (DLNR, 18 January 2023; the State's copyright, the fact cited): 5.0 t (an ESTIMATE, taken as
 * what lies there, JUDGED).
 */
export const KAHOOLAWE_T = 5.0;

/** What lies on each shore at the game's opening, tonnes (JUDGED from the MEASURED counts above): Maui 12.3, Molokaʻi 14.8, Lānaʻi 32.4, Kahoʻolawe 5.0. */
export const OPENING_SHORE_T: Readonly<Record<ReefIsland, number>> = {
  maui: DFG_POINTS_2008.maui * PILE_T,
  molokai: DFG_POINTS_2008.molokai * PILE_T,
  lanai: DFG_POINTS_2008.lanai * PILE_T,
  kahoolawe: KAHOOLAWE_T,
};

/**
 * What comes ashore again, tonnes a km of coast a year (JUDGED from a MEASURED removal): NOAA's Hawaiʻi Large Debris
 * Recovery, October 2021 to September 2023, "removed 61 metric tons (134,423 pounds) of marine debris through 142
 * community cleanups and 216 'net patrols' from 623 miles of coastlines across three islands" (NOAA Marine Debris
 * Program, a US government work): 61 t over 1,002.6 km in two years, 0.0304 t a km a year, taken as the shores'
 * accumulation (shores cleaned on a round take about what arrives). Times each island's coast (`COASTLINE_KM`): Maui
 * 5.9, Molokaʻi 4.3, Lānaʻi 2.3, Kahoʻolawe 1.4 t a year. The check: Kanapou's cleanups "every three years or so" took
 * about five tons (DLNR, 2023), 1.7 t a year, near Kahoʻolawe's 1.4.
 */
export const ASHORE_T_PER_KM_YEAR = 0.0304;

/**
 * What a crew clears in a day, tonnes (JUDGED from an ESTIMATE): KIRC's grant years, "the removal of an estimated 10
 * tons" over "five clean-up events" that "required four days each" (NOAA Marine Debris Program, Kahoʻolawe, 2013 to
 * 2015), 0.5 t a day, a crew with volunteers and a helicopter's lift; the conservancy's captain and two divers on its
 * boat taken at that rate, no more than lies there.
 */
export const CLEARED_T_PER_DAY = 0.5;

/** An island's shore's last reckoning: the last cleanup's, or the opening's on the world's first day (none stored until a cleanup, so a world no cleanup touched holds nothing new). */
export function shoreReckoning(world: Pick<World, 'operations' | 'startEpochDay'>, island: ReefIsland): ShoreReckoning {
  return world.operations.shores?.[island] ?? { tonnes: OPENING_SHORE_T[island], day: world.startEpochDay };
}

/** The tonnes on an island's shore today: its last reckoning and what has come ashore since, a year by definition 365 days. */
export function shoreDebrisT(world: Pick<World, 'operations' | 'startEpochDay' | 'day'>, island: ReefIsland): number {
  const r = shoreReckoning(world, island);
  return r.tonnes + (ASHORE_T_PER_KM_YEAR * COASTLINE_KM[island] * (epochDayOf(world) - r.day)) / 365;
}

/** The tonnes a cleanup of some days clears from an island's shore today: half a tonne a day, no more than lies there. */
export const cleanupClears = (world: Pick<World, 'operations' | 'startEpochDay' | 'day'>, island: ReefIsland, days: number): number => Math.min(CLEARED_T_PER_DAY * days, shoreDebrisT(world, island));

/** A cleanup home today: what it cleared taken off the island's shore, the reckoning set to what is left that day; the tonnes cleared. */
export function clearShore(world: World, island: ReefIsland, days: number): number {
  const cleared = cleanupClears(world, island, days);
  const left = shoreDebrisT(world, island) - cleared;
  world.operations.shores = { ...world.operations.shores, [island]: { tonnes: left, day: epochDayOf(world) } };
  return cleared;
}
